import {
	Entities,
	EntityStatus,
	FeedApi,
	FeedCorrection,
	FeedCorrectionListInData,
	FeedTank,
	FeedTankGraphItem,
	FeedTankInstallDataEntityTypeEnum,
	FeedTypeLevelMetricEnum,
	Pagination,
	ProductsApi,
	RawData,
	Sensor,
	Transceiver,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import { EntityStatusService } from '@/services/entity-status-service';
import { FeedTankCurrentState } from '../models';
import FeedTankCorrectionsList from '@/pages/account/products/feed/components/tabs/feed-tanks/components/corrections/FeedTankCorrectionsList.vue';

export class FeedTankService extends EntityStatusService<FeedTank> {
	private feedApi: FeedApi = new FeedApi();
	private productApi: ProductsApi = new ProductsApi();

	public entityReadyForSignOff(): boolean {
		if (
			this.entity.status === EntityStatus.Install &&
			this.feedTankIsProvisioned &&
			this.hardwareAllocated
		) {
			return true;
		}
		return false;
	}

	public get feedTankIsProvisioned(): boolean {
		const { nominalVolume, nominalWeight, feedTypeId, feedtype } = this.entity;
		if (feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume) {
			return nominalVolume != null && feedTypeId != null;
		} else {
			return nominalWeight != null && feedTypeId != null;
		}
	}

	public async getCurrentState(
		feedTank: FeedTank,
	): Promise<{ data?: FeedTankCurrentState; errorMessage?: string }> {
		if (!feedTank.id) {
			return { errorMessage: 'This feed tank has no id' };
		}
		try {
			const currentStateResponse = await this.feedApi.feedTanksIdCurrentStateGet(feedTank.id);
			return { data: currentStateResponse.data.data ?? {} };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to fetch feed tank current state');
		}
	}

	public async getTankRawData(
		feedTank: FeedTank,
		series: string = 'level',
		sortKey?: string,
		prev: boolean = false,
	): Promise<{ data?: { data: Array<RawData>; links?: Pagination }; errorMessage?: string }> {
		if (!feedTank.id) {
			return { errorMessage: 'This feed tank has no id' };
		}
		try {
			const currentStateResponse = await this.feedApi.getFeedTankRawData(feedTank.id, series, {
				params: {
					sortKey: sortKey?.length ? sortKey ?? undefined : undefined,
					prev,
				},
			});
			return {
				data: {
					data: currentStateResponse.data.data ?? [],
					links: <Pagination>currentStateResponse.data?.links,
				},
			};
		} catch (e) {
			return ApiUtils.handleApiError(
				e,
				`Failed to fetch feed tank graph data for series '${series}'`,
			);
		}
	}

	public async cancelFeedTank(
		feedTank: FeedTank,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id || !feedTank.productId) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.productApi.changeProductStatus(
				feedTank.productId,
				Entities.FeedTanks,
				feedTank.id,
				{ newStatus: EntityStatus.Cancelled, actionReason: actionReason ?? undefined },
			);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to cancel feed tank');
		}
	}

	public async removeFeedTank(
		feedTank: FeedTank,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.feedApi.feedTankDeactivate(feedTank.id, {
				actionReason: actionReason ?? undefined,
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to remove feed tank');
		}
	}

	public async moveFeedTankToSignOff(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.productId || !feedTank.id) {
			return { errorMessage: 'This feed tank has no associated product' };
		}
		try {
			await this.productApi.changeProductStatus(
				feedTank.productId,
				Entities.FeedTanks,
				feedTank.id,
				{ newStatus: EntityStatus.SignOff },
			);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to move vat to sign off');
		}
	}

	public async signOffFeedTank(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.feedApi.feedTankSignOff(feedTank.id);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to sign off feed tank');
		}
	}

	public async provision(feedTank: FeedTank & { sensorType?: 'radar' | 'strain' }): Promise<{ errorMessage: string } | void> {
		if (this.missingFeedTankAttributes(feedTank) || !feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			const isRadar = !feedTank.sensorType || feedTank.sensorType === 'radar';
			const isLevelMetricVolume = feedTank.feedtype?.levelMetric === 'volume';

			// Common fields
			const data: any = {
				name: feedTank.name,
				sensorType: feedTank.sensorType ?? null,
				type: feedTank.type ?? null,
				numTanks: feedTank.numTanks ?? null,
				feedTypeId: feedTank.feedTypeId ?? null,
			};

			if (isRadar) {
				// RADAR: feedTypeId, formulaId, formulaParameters required
				if (isLevelMetricVolume) {
					data.nominalVolume = feedTank.nominalVolume ?? null; // integer
				} else {
					data.nominalWeight = feedTank.nominalWeight ?? null; // integer
				}
				data.formulaId = +(feedTank.formulaId ?? 0);
				data.formulaParameters = feedTank.formulaParameters ?? {};
				
			} else {
				// STRAIN: nominalWeight required (already in kg from modal)
				if (feedTank.nominalWeight != null) {
					data.nominalWeight = feedTank.nominalWeight; // integer
				}
			}

			await this.feedApi.feedTankProvision(feedTank.id, {
				data: data,
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to provision feed tank');
		}
	}

	public async install(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id || !feedTank.transceiver?.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		} 
		const sensorIds = (feedTank.sensors ?? []).map((s) => s.id);
		if (new Set(sensorIds).size !== sensorIds.length) {
			return { errorMessage: 'Sensor list contains duplicate sensor IDs' };
		}
		try {
			await this.feedApi.feedTankInstall(feedTank.id, {
				data: {
					entityType: FeedTankInstallDataEntityTypeEnum.FeedTanks,
					sensorId: feedTank.sensor?.id!,
					sensors: (feedTank.sensors ?? []).map((sensor) => ({ id: sensor.id, type: sensor.type })),
					transceiverId: feedTank.transceiver.id,
					nodeId: feedTank.sensor?.node?.id ?? null,
				},
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to install feed tank');
		}
	}

	public async getCorrections(
		feedTank: FeedTank,
	): Promise<{ errorMessage?: string; data?: FeedCorrection[] }> {
		if (!feedTank?.id) return { errorMessage: 'Feed tank ID is required' };

		try {
			const corrections = (await this.feedApi.getFeedTankCorrections(feedTank.id)).data;
			return {
				data: corrections.data,
			};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to get feed tank corrections');
		}
	}

	public async updateFeedTankFormulaCorrection(
		feedTank: FeedTank,
		correction: FeedCorrection,
	): Promise<{ errorMessage?: string; data?: FeedTank }> {
		if (!feedTank?.id) return { errorMessage: 'Feed tank ID is required' };

		try {
			const updatedFeedTank = (
				await this.feedApi.updateFeedTankFormulaCorrection(feedTank.id, {
					formulaCorrection: correction,
				})
			).data?.data;
			return {
				data: updatedFeedTank,
			};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to update feed tank formula correction');
		}
	}

	private get hardwareAllocated(): boolean {
		return !!this.entity.transceiver?.id && !!this.entity.sensor;
	}

	private missingFeedTankAttributes(feedTank: FeedTank & { sensorType?: 'radar' | 'strain' }): boolean {
		const isRadar = feedTank.sensorType === 'radar';

		if (isRadar) {
			// RADAR requires: nominalVolume, feedTypeId, formulaId
			return (
				(feedTank.nominalVolume == null &&
					feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume) ||
				(feedTank.nominalWeight == null &&
					feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight) ||
				feedTank.feedTypeId == null ||
				feedTank.formulaId == null
			);
		} else {
			// STRAIN requires: nominalWeight only
			return (feedTank.nominalWeight == null &&
				feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight)
		}
	}
}
